// TaskActivityCompact.jsx - Compact activity feed component
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Skeleton,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Circle as StatusIcon,
  Person as AssignIcon,
  CalendarToday as DateIcon,
  Flag as PriorityIcon,
  Comment as CommentIcon,
  Edit as EditIcon,
  Add as CreateIcon,
  CheckCircle as DoneIcon,
  AttachFile as AttachmentIcon,
  Delete as DeleteIcon,
  Group as CollaboratorIcon,
  Timer as TimerIcon,
  CheckBox as ChecklistIcon,
  AccountTree as SubtaskIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import useActivityStore from '../../../stores/activityStore';

// Activity type configuration - handles both 'type' and 'activityType' fields
const ACTIVITY_CONFIG = {
  // Task activity types
  created: { 
    icon: CreateIcon, 
    color: '#10B981',
    getText: (details, user) => `${user} created this task`
  },
  status_changed: { 
    icon: StatusIcon, 
    color: '#3B82F6',
    getText: (details, user) => `${user} changed status to ${details.to || 'unknown'}`
  },
  assigned: { 
    icon: AssignIcon, 
    color: '#F59E0B',
    getText: (details, user) => `${user} assigned to ${details.assignee || 'someone'}`
  },
  comment: { 
    icon: CommentIcon, 
    color: '#8B5CF6',
    getText: (details, user) => `${user} commented${details.isInternal ? ' (internal)' : ''}`
  },
  comment_added: { 
    icon: CommentIcon, 
    color: '#8B5CF6',
    getText: (details, user) => `${user} commented${details.isInternal ? ' (internal)' : ''}`
  },
  priority_changed: { 
    icon: PriorityIcon, 
    color: '#EF4444',
    getText: (details, user) => `${user} set priority to ${details.to || 'unknown'}`
  },
  due_date_changed: { 
    icon: DateIcon, 
    color: '#06B6D4',
    getText: (details, user) => details.dueDate ? `${user} set due date` : `${user} removed due date`
  },
  attachment_added: {
    icon: AttachmentIcon,
    color: '#EC4899',
    getText: (details, user) => `${user} added attachment${details.filename ? ` "${details.filename}"` : ''}`
  },
  attachment_removed: {
    icon: DeleteIcon,
    color: '#64748B',
    getText: (details, user) => `${user} removed attachment`
  },
  checklist_updated: {
    icon: ChecklistIcon,
    color: '#84CC16',
    getText: (details, user) => `${user} updated checklist item`
  },
  subtask_added: {
    icon: SubtaskIcon,
    color: '#14B8A6',
    getText: (details, user) => `${user} added subtask${details.title ? ` "${details.title}"` : ''}`
  },
  collaborator_added: {
    icon: CollaboratorIcon,
    color: '#F97316',
    getText: (details, user) => `${user} added ${details.collaborator || 'someone'} as collaborator`
  },
  timer_started: {
    icon: TimerIcon,
    color: '#0EA5E9',
    getText: (details, user) => `${user} started timer`
  },
  timer_stopped: {
    icon: TimerIcon,
    color: '#64748B',
    getText: (details, user) => `${user} stopped timer${details.duration ? ` (${details.duration})` : ''}`
  },
  // Status types
  status: {
    icon: StatusIcon,
    color: '#3B82F6',
    getText: (details, user) => `${user} changed status`
  },
  priority: {
    icon: PriorityIcon,
    color: '#EF4444',
    getText: (details, user) => `${user} changed priority`
  },
  // Default fallback
  updated: {
    icon: EditIcon,
    color: '#64748B',
    getText: (details, user) => `${user} updated the task`
  },
  update: {
    icon: EditIcon,
    color: '#64748B',
    getText: (details, user) => `${user} updated the task`
  }
};

// Helper to format user name
const formatUserName = (user) => {
  if (!user) return 'Someone';
  if (typeof user === 'string') return 'User';
  // Handle both fullName and firstName/lastName
  if (user.fullName) return user.fullName;
  if (user.name) return user.name;
  return `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Someone';
};

// Helper to safely format detail values
const formatDetailValue = (value) => {
  if (!value) return '';
  if (typeof value === 'string' || typeof value === 'number') return value;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value instanceof Date) return value.toLocaleDateString();
  if (typeof value === 'object') {
    // Handle specific object types
    if (value.name) return value.name;
    if (value.title) return value.title;
    if (value.firstName && value.lastName) return `${value.firstName} ${value.lastName}`;
    if (value.status) return value.status;
    // For other objects, try to extract meaningful info
    return JSON.stringify(value).substring(0, 50);
  }
  return String(value);
};

/**
 * TaskActivityCompact - Compact, clean activity feed
 * Consistent with the enhanced UI design of other tabs
 */
const TaskActivityCompact = ({ taskId }) => {
  const theme = useTheme();
  const [showAll, setShowAll] = useState(false);
  
  // Activity store
  const {
    taskActivities,
    fetchTaskActivities,
    isLoading,
  } = useActivityStore();
  
  // Get activities for this task
  const activities = taskActivities[taskId] || [];
  const visibleActivities = showAll ? activities : activities.slice(0, 5);
  const hiddenCount = activities.length - visibleActivities.length;
  
  // Load activities
  useEffect(() => {
    if (taskId) {
      fetchTaskActivities(taskId);
    }
  }, [taskId, fetchTaskActivities]);
  
  // Debug: Log activities to see what data we have
  useEffect(() => {
    if (activities.length > 0) {
      console.log('Task activities:', activities);
      console.log('First activity structure:', activities[0]);
    }
  }, [activities]);
  
  // Loading state
  if (isLoading) {
    return (
      <Box sx={{ 
        bgcolor: alpha(theme.palette.background.paper, 0.3),
        borderRadius: 1,
        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        p: 1.5,
      }}>
        {[1, 2, 3].map((i) => (
          <Skeleton 
            key={i} 
            variant="text" 
            height={20} 
            sx={{ my: 0.5 }} 
          />
        ))}
      </Box>
    );
  }
  
  // Empty state
  if (activities.length === 0) {
    return (
      <Box sx={{ 
        bgcolor: alpha(theme.palette.background.paper, 0.3),
        borderRadius: 1,
        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        p: 1.5,
        textAlign: 'center',
      }}>
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ fontStyle: 'italic' }}
        >
          No activity recorded yet
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ 
      bgcolor: alpha(theme.palette.background.paper, 0.3),
      borderRadius: 1,
      border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
      p: 1.5,
    }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {visibleActivities.map((activity, index) => {
          if (!activity) return null;
          
          // Use activityType for icon/color, but description for text
          const activityType = activity.activityType || activity.type || 'updated';
          const config = ACTIVITY_CONFIG[activityType] || ACTIVITY_CONFIG.updated;
          const Icon = config.icon;
          
          // Get user name
          const userName = formatUserName(activity.user);
          
          // Build activity text - always include user name
          let activityText;
          if (activity.description) {
            // Description like "created task \"test\"" - prepend user name
            activityText = `${userName} ${activity.description}`;
          } else {
            // Fallback to generating text from config
            activityText = config.getText ? 
              config.getText(activity.details || {}, userName) : 
              `${userName} updated the task`;
          }
          
          return (
            <Box
              key={activity._id || index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.5,
                px: 0.5,
                borderRadius: 0.5,
                '&:hover': {
                  bgcolor: alpha(theme.palette.action.hover, 0.04),
                },
                transition: 'background-color 0.15s ease',
                cursor: 'default',
              }}
            >
              <Icon 
                sx={{ 
                  fontSize: 14, 
                  color: config.color,
                  opacity: 0.9,
                  flexShrink: 0,
                }}
              />
              <Typography 
                variant="body2" 
                sx={{ 
                  flex: 1,
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                  lineHeight: 1.4,
                }}
              >
                {activityText}
                {/* Show additional details if available */}
                {activity.details?.before !== undefined && activity.details?.after !== undefined && (
                  <Typography component="span" sx={{ fontSize: '0.7rem', color: 'text.disabled', ml: 0.5 }}>
                    ({formatDetailValue(activity.details.before)} → {formatDetailValue(activity.details.after)})
                  </Typography>
                )}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: '0.65rem',
                  color: 'text.disabled',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {activity.createdAt ? 
                  formatDistanceToNow(new Date(activity.createdAt), { addSuffix: true }) : 
                  'Unknown time'
                }
              </Typography>
            </Box>
          );
        })}
      </Box>
      
      {/* Show more/less button */}
      {hiddenCount > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Button
            size="small"
            onClick={() => setShowAll(!showAll)}
            startIcon={showAll ? <CollapseIcon sx={{ fontSize: 14 }} /> : <ExpandIcon sx={{ fontSize: 14 }} />}
            sx={{
              fontSize: '0.7rem',
              textTransform: 'none',
              color: 'text.secondary',
              py: 0.25,
              px: 1.5,
              '&:hover': {
                bgcolor: alpha(theme.palette.action.hover, 0.08),
              },
            }}
          >
            {showAll ? 'Show less' : `Show ${hiddenCount} more`}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default TaskActivityCompact;